"use client";
import type { ReactNode } from "react";
import Image from "next/image";
import Link from "next/link";
import { FindWorkshopButtonClient } from "@/components/shared/FindWorkshopButton.client";
import { useLanguage } from "@/contexts/LanguageContext";
import type { ListedOffer } from "@/lib/offersApi";
import { formatDateV2 } from "@/lib/formatDateV2";
import { GTMEvents, GTMSubEvents, PushDataToGTM } from "@/lib/gtm";

function OfferValidityClockIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width={15}
      height={22}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  );
}

export function OfferBadge({
  offer,
  labelFallback,
  textOverride,
  content,
  accessibilityLabel,
  className,
}: {
  /** Listing cards pass full offer; ticket detail passes `{ subtitle }` only. */
  offer: Pick<ListedOffer, "subtitle">;
  labelFallback: string;
  /** When set (e.g. currency-resolved subtitle), used instead of `offer.subtitle`. */
  textOverride?: string;
  /** When set, shown instead of plain subtitle (e.g. riyal symbol + amount). */
  content?: ReactNode;
  /** Plain string for `title` / sr-only when `content` is not a simple string. */
  accessibilityLabel?: string;
  /** Extra rounding when overlaid on a rounded image (e.g. offer ticket hero). */
  className?: string;
}) {
  const fallbackText = (textOverride ?? offer.subtitle).trim() || labelFallback;
  const display = content ?? fallbackText;
  const a11y =
    accessibilityLabel?.trim() ||
    (typeof display === "string" ? display : fallbackText);
  const copies = 4;
  return (
    <div
      className={`pointer-events-none absolute inset-x-0 bottom-0 z-10 overflow-hidden bg-[#BD2226]/20 py-2 backdrop-blur-[2px] ${className ?? ""}`}
      title={a11y}
    >
      <div className="animate-offer-tag-marquee">
        {Array.from({ length: copies }, (_, i) => (
          <span
            key={i}
            className="inline-flex shrink-0 items-center px-8 text-xs font-bold tracking-wide text-white whitespace-nowrap"
          >
            <span className="text-white/50 pe-3" aria-hidden>
              •
            </span>
            {display}
          </span>
        ))}
      </div>
      <span className="sr-only">{a11y}</span>
    </div>
  );
}

const DEFAULT_IMAGE_SIZES =
  "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, (max-width: 1280px) 33vw, 25vw";

export type OfferListingCardProps = {
  offer: ListedOffer;
  href: string;
  specialOfferLabel: string;
  expiredLabel: string;
  couponAvailableLabel: string;
  className?: string;
  /** Override for narrow strips (e.g. home carousel). */
  imageSizes?: string;
  /** Marquee / badge line (e.g. refer-a-friend with currency symbol). */
  marqueeTextOverride?: string;
  pageName: string;
  pageCategory: string;
  eventCategory: string;
};

const FIND_WORKSHOP_BTN_CLASS =
  "relative z-20 inline-flex h-[42px] max-w-full flex-1 items-center justify-center whitespace-normal rounded-lg bg-[#BD2226] px-2.5 text-center text-[11px] font-semibold leading-tight text-white shadow-sm ring-1 ring-black/5 transition-colors hover:bg-[#C00322] sm:px-3 sm:text-xs sm:leading-none";

const VIEW_OFFER_LINK_CLASS =
  "inline-flex h-[42px] flex-1 items-center justify-center rounded-lg bg-[#BD2226] px-2.5 text-center text-[11px] font-semibold leading-tight text-white shadow-sm ring-1 ring-black/5 transition-colors hover:bg-[#C00322] sm:px-3 sm:text-xs sm:leading-none";

export function OfferListingCard({
  offer,
  href,
  specialOfferLabel,
  expiredLabel,
  couponAvailableLabel,
  className,
  imageSizes = DEFAULT_IMAGE_SIZES,
  marqueeTextOverride,
  pageName,
  pageCategory,
  eventCategory,
}: OfferListingCardProps) {
  const { t, locale } = useLanguage();
  const validityEnd = formatDateV2(offer.dateTo, locale);

  return (
    <div
      className={`flex h-full flex-col overflow-hidden rounded-lg border border-[#E5E5E5] bg-white transition-shadow hover:shadow-lg group ${className ?? ""}`}
    >
      <Link
        href={href}
        prefetch
        onClick={() => {
          PushDataToGTM(GTMEvents.DataLayerUpdate, {
            pageName: pageName,
            pageCategory: pageCategory,
            eventCategory: GTMSubEvents.Click,
            eventLabel: pageName + "> " + offer.name.toUpperCase(),
            offer: offer.name.toUpperCase(),
          });
        }}
        className="block shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#DD0428]/30 focus-visible:ring-offset-2"
      >
        <div className="relative aspect-4/3 w-full bg-[#F4F4F4]">
          <Image
            src={offer.image}
            alt={offer.name}
            fill
            className="object-cover transition-transform duration-300 group-hover:scale-105"
            sizes={imageSizes}
          />
          <OfferBadge
            offer={offer}
            labelFallback={specialOfferLabel}
            textOverride={marqueeTextOverride}
          />
        </div>
      </Link>

      <div className="flex min-h-0 flex-1 flex-col p-4">
        <Link
          href={href}
          prefetch
          onClick={() => {
            PushDataToGTM(GTMEvents.DataLayerUpdate, {
              pageName: pageName,
              pageCategory: pageCategory,
              eventCategory: GTMSubEvents.Click,
              eventLabel: pageName + ">" + offer.name.toUpperCase(),
              offer: offer.name.toUpperCase(),
            });
          }}
          className="mb-1 line-clamp-2 font-semibold text-[#130F0E] transition-colors hover:text-[#BD2226]"
        >
          {offer.name}
        </Link>
        <p className="mb-3 line-clamp-2 text-sm text-[#6B7280]">
          {offer.description || "\u00a0"}
        </p>
        {offer.couponEnabled ? (
          <span className="mb-3 inline-block rounded bg-[#F4F4F4] px-2 py-1 font-mono text-xs text-[#130F0E]">
            {couponAvailableLabel}
          </span>
        ) : null}

        <div className="min-h-0 flex-1" aria-hidden />

        <div className="mt-auto shrink-0">
          {validityEnd || offer.isExpired ? (
            <div className="mb-3 inline-flex items-center gap-2 text-[15px] font-semibold leading-[22px] text-[#FF8300]">
              <OfferValidityClockIcon className="h-[15px] w-[15px] shrink-0" />
              <span className="whitespace-nowrap">
                {offer.isExpired ? expiredLabel : validityEnd}
              </span>
            </div>
          ) : null}
          <div className="flex gap-2">
            <Link
              href={href}
              prefetch
              className={VIEW_OFFER_LINK_CLASS}
              onClick={() => {
                PushDataToGTM(GTMEvents.DataLayerUpdate, {
                  pageName: pageName,
                  pageCategory: pageCategory,
                  eventCategory: GTMSubEvents.Click,
                  eventLabel: pageName + " > VIEW OFFER",
                  offer: offer.name.toUpperCase(),
                });
              }}
            >
              {t("offers.viewOffer")}
            </Link>
            <FindWorkshopButtonClient
              label={t("hero.bookService")}
              leadContext={{
                category: "Offer",
                pageType: "Offer Listing",
                leadTypeId: 4,
                typeId: offer.id,
              }}
              className={FIND_WORKSHOP_BTN_CLASS}
              pageName={pageName}
              pageCategory={pageCategory}
              eventCategory={eventCategory}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
